/* ============================================================
   SINGSPACE ADMIN - PAGE CONNEXION PREMIUM (Liquid Glass)
   ============================================================ */

import { Lock, ShieldTick, Sms } from 'iconsax-react';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const { login } = useAuth();
  const navigate = useNavigate();

  // Charger l'email sauvegardé au montage
  useEffect(() => {
    const savedEmail = localStorage.getItem('savedEmail');
    const rememberMeValue = localStorage.getItem('rememberMe');

    if (rememberMeValue === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Effet de parallaxe sur le logo
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);

      // Gestion du "Se souvenir de moi"
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('savedEmail', email);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('savedEmail');
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="premium-auth-container">
      {/* Animated Background */}
      <div className="premium-bg-animated">
        <div className="premium-gradient-orb premium-orb-1"></div>
        <div className="premium-gradient-orb premium-orb-2"></div>
        <div className="premium-gradient-orb premium-orb-3"></div>
      </div>

      {/* Left side - Premium Experience */}
      <div className="premium-auth-left">
        <div className="premium-auth-overlay"></div>

        {/* Floating particles */}
        <div className="premium-particles">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="premium-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
              }}
            ></div>
          ))}
        </div>

        <div className="premium-auth-content">
          {/* Logo avec effet liquid glass et parallaxe */}
          <div
            className="premium-logo-container"
            style={{
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            }}
          >
            <div className="premium-logo-glass">
              <div className="premium-logo-glow"></div>
              <img src="/logo-singspace-blanc.png" alt="SingSpace" className="premium-logo-img" />
            </div>
          </div>

          {/* Texte avec effet morphing */}
          <div className="premium-text-container">
            <h1 className="premium-title">
              <span className="premium-title-line">Espace Administrateur</span>
              <span className="premium-title-brand">SingSpace</span>
            </h1>
            <p className="premium-subtitle">Plateforme de gestion des espaces de coworking et d'incubation</p>
          </div>

          {/* Logo SING en bas */}
          <div className="premium-sing-footer">
            <img src="/assets/lgo_blan.png" alt="SING SA" className="premium-sing-logo" />
            <p className="premium-sing-text">Société d'Incubation Numérique du Gabon</p>
          </div>
        </div>
      </div>

      {/* Right side - Premium Form */}
      <div className="premium-auth-right">
        <div className="premium-form-container">
          {/* Logo mobile */}
          <div className="premium-logo-mobile">
            <img src="/logo-singspace.png" alt="SingSpace" />
          </div>

          {/* Header avec badge premium */}
          <div className="premium-header">
            <h2 className="premium-form-title">Connexion</h2>
            <div className="premium-admin-badge">
              <div className="premium-badge-shine"></div>
              <ShieldTick size={18} variant="Bold" />
              <span>Espace Administrateur</span>
              <div className="premium-badge-pulse"></div>
            </div>
          </div>

          {/* Error alert premium */}
          {error && (
            <div className="premium-alert premium-alert-error">
              <div className="premium-alert-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <span>{error}</span>
            </div>
          )}

          {/* Premium Form */}
          <form onSubmit={handleSubmit} className="premium-form">
            {/* Email avec effet liquid */}
            <div className="premium-form-group">
              <label htmlFor="email" className="premium-label">
                Adresse email
              </label>
              <div className="premium-input-wrapper">
                <div className="premium-input-bg"></div>
                <Sms size={20} variant="Bold" className="premium-input-icon" />
                <input
                  type="email"
                  id="email"
                  placeholder="admin@sing.ga"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="premium-input"
                />
                <div className="premium-input-border"></div>
              </div>
            </div>

            {/* Password avec toggle visibility */}
            <div className="premium-form-group">
              <label htmlFor="password" className="premium-label">
                Mot de passe
              </label>
              <div className="premium-input-wrapper">
                <div className="premium-input-bg"></div>
                <Lock size={20} variant="Bold" className="premium-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="premium-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="premium-password-toggle"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                <div className="premium-input-border"></div>
              </div>
            </div>

            {/* Remember Me premium */}
            <div className="premium-remember-me">
              <label className="premium-checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="premium-checkbox-input"
                />
                <span className="premium-checkbox-custom">
                  <span className="premium-checkbox-checkmark"></span>
                </span>
                <span className="premium-checkbox-text">Se souvenir de moi</span>
              </label>
            </div>

            {/* Submit button premium */}
            <button type="submit" className="premium-btn-submit" disabled={isLoading}>
              <span className="premium-btn-bg"></span>
              <span className="premium-btn-shine"></span>
              <span className="premium-btn-text">
                {isLoading ? (
                  <>
                    <span className="premium-spinner"></span>
                    <span>Connexion...</span>
                  </>
                ) : (
                  <span>Se connecter</span>
                )}
              </span>
            </button>

            {/* Forgot password premium */}
            <Link to="/forgot-password" className="premium-link">
              <span className="premium-link-text">Mot de passe oublié ?</span>
              <span className="premium-link-underline"></span>
            </Link>
          </form>

          {/* Footer premium */}
          <div className="premium-footer">
            <div className="premium-footer-line"></div>
            <p className="premium-footer-text">
              © {new Date().getFullYear()} SING SA - Société d'Incubation Numérique du Gabon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
